import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from "../firebase";
import { signInWithPopup, signOut, onAuthStateChanged, User, GoogleAuthProvider } from "firebase/auth";
import { setDoc, getDoc, doc, serverTimestamp } from "firebase/firestore";

interface FirebaseContextType {
  user: User | null;
  loading: boolean;
  signIn: () => Promise<void>;
  logout: () => Promise<void>;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Sync user profile to Firestore without overwriting existing fields
        const userRef = doc(db, 'users', currentUser.uid);
        try {
          const existingDoc = await getDoc(userRef);
          if (existingDoc.exists()) {
            // Existing user — only update lastLogin and basic info, preserve role/staffRole/address/createdAt
            const existingData = existingDoc.data();
            const patch: Record<string, any> = {
              displayName: currentUser.displayName || existingData.displayName || 'Anonymous',
              email: currentUser.email || existingData.email,
              lastLogin: serverTimestamp(),
            };
            const photo = currentUser.photoURL || existingData.photoURL;
            if (photo) patch.photoURL = photo;
            await setDoc(userRef, patch, { merge: true });
          } else {
            // Profile creation belongs to AuthView/App, which write the required
            // role/accountType fields. A bare profile here is denied by the
            // Firestore rules and only creates noise.
          }
        } catch (error) {
          // We don't want to block the app if profile sync fails, but we should log it
          console.error('Error syncing user profile:', error);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  return (
    <FirebaseContext.Provider value={{ user, loading, signIn, logout }}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};
